/**
 * CustomSelect Component
 * 사용자 친화적인 커스텀 셀렉트 컴포넌트
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../styles/CustomSelect.css';

export interface SelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface CustomSelectProps {
    options: SelectOption[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    disabled?: boolean;
    className?: string;
    maxVisibleOptions?: number;
    searchable?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = '선택하세요',
    label,
    disabled = false,
    className = '',
    maxVisibleOptions = 6,
    searchable = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // 선택된 옵션 찾기
    const selectedOption = options.find(opt => opt.value === value);

    // 필터링된 옵션
    const filteredOptions = searchable && searchQuery
        ? options.filter(opt =>
            opt.label.toLowerCase().includes(searchQuery.toLowerCase())
        )
        : options;

    // 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 드롭다운 열릴 때 검색 input 포커스
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // 하이라이트된 옵션 스크롤
    useEffect(() => {
        if (isOpen && listRef.current && highlightedIndex >= 0) {
            const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
            if (highlightedElement) {
                highlightedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, isOpen]);

    // 토글 핸들러
    const handleToggle = useCallback(() => {
        if (!disabled) {
            setIsOpen(prev => !prev);
            setSearchQuery('');
            setHighlightedIndex(-1);
        }
    }, [disabled]);

    // 옵션 선택 핸들러
    const handleSelect = useCallback((optionValue: string, optionDisabled?: boolean) => {
        if (optionDisabled) return;
        onChange(optionValue);
        setIsOpen(false);
        setSearchQuery('');
    }, [onChange]);

    // 키보드 핸들러
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (disabled) return;

        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen && highlightedIndex >= 0) {
                    const option = filteredOptions[highlightedIndex];
                    if (option && !option.disabled) {
                        handleSelect(option.value);
                    }
                } else {
                    setIsOpen(true);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSearchQuery('');
                break;
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen) {
                    setIsOpen(true);
                } else {
                    setHighlightedIndex(prev =>
                        prev < filteredOptions.length - 1 ? prev + 1 : 0
                    );
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                if (isOpen) {
                    setHighlightedIndex(prev =>
                        prev > 0 ? prev - 1 : filteredOptions.length - 1
                    );
                }
                break;
        }
    }, [disabled, isOpen, highlightedIndex, filteredOptions, handleSelect]);

    // 옵션 높이 계산 (최대 표시 개수 기반)
    const optionHeight = 44; // px
    const maxHeight = maxVisibleOptions * optionHeight;

    return (
        <div
            ref={containerRef}
            className={`custom-select-container ${className} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
            onKeyDown={handleKeyDown}
        >
            {label && <label className="custom-select-label">{label}</label>}

            <div
                className="custom-select-trigger"
                onClick={handleToggle}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
            >
                <span className={`custom-select-value ${!selectedOption ? 'placeholder' : ''}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <span className="custom-select-arrow">
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M2.5 4.5L6 8L9.5 4.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </span>
            </div>

            {isOpen && (
                <div className="custom-select-dropdown">
                    {searchable && (
                        <div className="custom-select-search">
                            <input
                                ref={searchInputRef}
                                type="text"
                                className="custom-select-search-input"
                                placeholder="검색..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setHighlightedIndex(0);
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    )}

                    <ul
                        ref={listRef}
                        className="custom-select-options"
                        role="listbox"
                        style={{ maxHeight: `${maxHeight}px` }}
                    >
                        {filteredOptions.length === 0 ? (
                            <li className="custom-select-no-results">
                                검색 결과가 없습니다
                            </li>
                        ) : (
                            filteredOptions.map((option, index) => (
                                <li
                                    key={option.value}
                                    className={`custom-select-option ${option.value === value ? 'selected' : ''} ${option.disabled ? 'disabled' : ''} ${index === highlightedIndex ? 'highlighted' : ''}`}
                                    onClick={() => handleSelect(option.value, option.disabled)}
                                    role="option"
                                    aria-selected={option.value === value}
                                >
                                    <span className="custom-select-option-label">{option.label}</span>
                                    {option.value === value && (
                                        <span className="custom-select-check">
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M3.5 8L6.5 11L12.5 5"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                    )}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default CustomSelect;
